'use client';

import type React from 'react';

import { type FormEvent, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import type { NameFiles } from 'src/features/createAccount/screens/CreateAccount/ui';
import { toCurrency } from 'src/helpers/currency/toCurrency';
import validators from 'src/helpers/validators';
import useForm from 'src/hooks/useForm';
import type { Brand } from 'src/model/Brand';
import type { Category } from 'src/model/Category';
import type { Dimensions } from 'src/model/Dimensions';
import type { Grade } from 'src/model/Grade';
import type { GradeSize } from 'src/model/GradeSize';
import { ProductStatus } from 'src/model/Product';
import type { Warranty } from 'src/model/Warranty';
import { setLoading, useLoading } from 'src/redux/loading/loadingSlice';
import { api } from 'src/services/api';
import {
  FormInputGrade,
  FormInputGradeSize,
  FormRegisterProduct,
  type ProductBasic,
} from '../../types/types';
import { CreateProductUI } from './ui';

type BrandCreate = Omit<Brand, 'id'>;
type DimensionsCreate = Omit<Dimensions, 'id'>;
type WarrantyCreate = Omit<Warranty, 'id'>;

type ProductPayload = Omit<
  ProductBasic,
  'brand' | 'dimensions' | 'warranty'
> & {
  brand: BrandCreate;
  dimensions: DimensionsCreate;
  warranty: WarrantyCreate;
};

interface AxiosError {
  response?: {
    data?: string | Record<string, unknown>;
  };
}

export const CreateProductScreen = () => {
  const { loading } = useSelector(useLoading);
  const dispatch = useDispatch();
  const [formNameFiles, setFormNameFiles] = useState<NameFiles>({});
  const [files, setFiles] = useState<File[]>([]);
  const [filesGrade, setFilesGrade] = useState<File[]>([]);
  const [filesBase64, setFilesBase64] = useState<string[]>([]);
  const [filesBase64Grade, setFilesBase64Grade] = useState<string>('');
  const [previewFiles, setPreviewFiles] = useState<string[]>([]);
  const [previewFilesGrade, setPreviewFilesGrade] = useState<string>('');
  const [previewGradesSizes, setPreviewGradesSizes] = useState<GradeSize[]>([]);
  const [inputTags, setInputTags] = useState<string[]>([]);
  const [category, setCategory] = useState<Category[]>([]);
  const [selectedCategories, setSelectedCategories] = useState<Category[]>([]);
  const [grade, setGrade] = useState<Grade[]>([]);
  const [gradeSize, setGradeSize] = useState<GradeSize[]>([]);
  const [shippingCost, setShippingCost] = useState<string | null>(null);
  const [isGrade, setIsGrade] = useState<boolean>(false);
  const [advancedGrades, setAdvancedGrades] = useState<any[]>([]);

  const getCategory = async (): Promise<void> => {
    try {
      dispatch(setLoading(true));
      const response = await api.get<Category[]>('/category/find-all');

      if (response.data) {
        setCategory(response.data);
      }
      // eslint-disable-next-line
    } catch (error: any) {
      let errorMessage =
        'Falha ao realizar o cadastro, tente novamente mais tarde';
      if (error.response && error.response.data) {
        errorMessage =
          typeof error.response.data === 'string'
            ? error.response.data
            : JSON.stringify(error.response.data);
      }
      toast.error(errorMessage);
      setErrorRegister({
        document: [errorMessage],
      });
    } finally {
      dispatch(setLoading(false));
    }
  };

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      const newFiles = Array.from(e.target.files);
      const totalFiles = [...files, ...newFiles];
      const images = totalFiles.filter(
        (file) => file.type.startsWith('image/') && totalFiles.length <= 4
      );
      const videos = totalFiles.filter((file) =>
        file.type.startsWith('video/')
      );

      if (images.length > 4 || videos.length > 1 || totalFiles.length > 5) {
        alert('Máximo de 4 imagens e 1 vídeo ou 5 arquivos no total.');
        return;
      }

      setFiles(totalFiles.slice(0, 5));

      totalFiles.forEach((file) => {
        const reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onload = () => {
          const base64 = reader.result?.toString();
          if (base64) {
            setFilesBase64((prevBase64) => [...prevBase64, base64]);
          }
        };
      });

      const previews = totalFiles.map((file) => URL.createObjectURL(file));
      setPreviewFiles(previews);
    }
  };

  const handleDeleteFile = (index: number) => {
    const updatedFiles = files.filter((_, i) => i !== index);
    setFiles(updatedFiles);
    const updatedPreviews = previewFiles.filter((_, i) => i !== index);
    setPreviewFiles(updatedPreviews);
  };

  const {
    formData: formDataRegister,
    formErrors: formErrorRegister,
    setErrors: setErrorRegister,
    onChangeFormInput: onChangeFormInputRegister,
    isFormValid: isFormValidRegister,
  } = useForm({
    initialData: {
      name: '',
      description: '',
      priceDiscount: '',
      sku: '',
      status: '',
      promotion: '',
      brandName: '',
      dimensionsWidth: '',
      dimensionsHeight: '',
      dimensionsDepth: '',
      dimensionsWeight: '',
      warranty: '',
      parcel: '12',
      tag: '',
      freeShipping: '',
      hiddenField: '',
      nameGrade: '',
      size: '',
      stock: '',
      price: '',
      discount: '',
      color: '',
      advancedGrades: '',
    },
    validators: {
      name: [validators.required],
      description: [validators.required],
      status: [validators.required],
      brandName: [validators.required],
      dimensionsWidth: [validators.required],
      dimensionsHeight: [validators.required],
      dimensionsDepth: [validators.required],
      dimensionsWeight: [validators.required],
      warranty: [validators.required],
      //stock: [validators.required],
      // mediaFiles: [validators.required],
    },
  });

  const handleOnChangeFileInput =
    (inputName: string) => (file: File | undefined) => {
      if (file && file.type.match(/image\/(jpg|jpeg|png)/)) {
        const reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onload = () => {
          const base64 = reader.result?.toString();
          if (base64) {
            setFormNameFiles({ ...formNameFiles, [inputName]: file.name });
            onChangeFormInputRegister(inputName)(base64);
          }
        };
      } else {
        setErrorRegister({
          [inputName]: ['O formato deve ser .jpg, .jpeg ou .png'],
        });
      }
    };

  const handleOnChangeFileInputGradeSize = (
    e: React.ChangeEvent<HTMLInputElement>
  ): void => {
    if (e.target.files && e.target.files.length > 0) {
      const file = e.target.files[0];

      if (!file.type.startsWith('image/')) {
        alert('Apenas arquivos de imagem são permitidos.');
        return;
      }

      setFilesGrade([file]);

      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => {
        const base64 = reader.result?.toString();
        if (base64) {
          setFilesBase64Grade(base64);
        }
      };
      const previews = URL.createObjectURL(file);
      setPreviewFilesGrade(previews);
    }
  };

  const handleStatusType = (value: string): ProductStatus => {
    switch (value) {
      case 'Ativo':
        return ProductStatus.ACTIVE;
      case 'Inativo':
        return ProductStatus.INACTIVE;
      case 'Estoque Zerado':
        return ProductStatus.DISCONTINUED;
      default:
        return ProductStatus.INACTIVE;
    }
  };

  const handleOnSubmitRegister = async (e: FormEvent): Promise<void> => {
    e.preventDefault();

    const isGradeFlag =
      String(
        (formDataRegister as Record<string, unknown>)['isGrade'] ?? isGrade
      ).toLowerCase() === 'true';

    if (!isGradeFlag) {
      const stock = String(
        (formDataRegister as Record<string, unknown>)['stock'] ?? ''
      );
      if (stock === '' || stock === '0') {
        toast.error('O estoque não pode ser zero!');
        setErrorRegister({ stock: ['O estoque não pode ser zero!'] });
        return;
      }
    }

    if (!isFormValidRegister()) {
      toast.error('Preencha todos os campos obrigatórios corretamente.');
      return;
    }

    const toStr = (v: unknown) => (typeof v === 'string' ? v : '');
    const norm = (s: string) =>
      s
        .normalize('NFD')
        .replace(/\p{Diacritic}/gu, '')
        .toLowerCase();
    const unique = (arr: string[]) => {
      const s = new Set<string>();
      const out: string[] = [];
      for (const x of arr)
        if (x && !s.has(x)) {
          s.add(x);
          out.push(x);
        }
      return out;
    };
    const parseJsonArray = (raw: unknown): any[] => {
      if (Array.isArray(raw)) return raw;
      if (typeof raw === 'string' && raw.trim().startsWith('[')) {
        try {
          return JSON.parse(raw);
        } catch {
          return [];
        }
      }
      return [];
    };
    const firstImgFrom = (arr: unknown[]): string => {
      for (const it of arr) {
        if (typeof it === 'string' && it) return it;
        if (it && typeof it === 'object') {
          const anyIt = it as any;
          if (typeof anyIt.url === 'string' && anyIt.url) return anyIt.url;
          if (typeof anyIt.src === 'string' && anyIt.src) return anyIt.src;
        }
      }
      return '';
    };
    const resolveColorKey = (keys: string[]) =>
      keys.find((k) => {
        const n = norm(k);
        return (
          n.includes('cor') ||
          n.includes('color') ||
          n.includes('variante') ||
          n.includes('modelo')
        );
      }) ||
      keys[0] ||
      'UNICO';
    const resolveSizeKey = (keys: string[], colorKey: string) =>
      keys.find((k) => {
        const n = norm(k);
        if (k === colorKey) return false;
        return (
          n.includes('tam') ||
          n.includes('tamanho') ||
          n.includes('size') ||
          n.includes('medida')
        );
      }) ||
      keys.find((k) => k !== colorKey) ||
      'unico';
    const pickVariantImage = (variant: Record<string, unknown>): string => {
      const mp = Array.isArray((variant as any)?.mediaPreview)
        ? (variant as any).mediaPreview
        : [];
      const m = Array.isArray((variant as any)?.media)
        ? (variant as any).media
        : [];
      const direct =
        typeof (variant as any)?.image === 'string'
          ? (variant as any).image
          : '';
      const chosen = firstImgFrom([...mp, ...m]);
      return chosen || direct || '';
    };

    const agFromState = Array.isArray(advancedGrades) ? advancedGrades : [];
    const agFromForm = parseJsonArray(
      (formDataRegister as Record<string, unknown>)['advancedGrades'] ??
        (formDataRegister as Record<string, unknown>)['grade']
    );
    const variants: Record<string, unknown>[] = isGradeFlag
      ? agFromState.length
        ? agFromState
        : agFromForm
      : [];

    const buildGrades = (): Grade[] => {
      if (!isGradeFlag) {
        const rawPrice = Number.parseFloat(
          String((formDataRegister as Record<string, unknown>)['price'] ?? '0')
        );
        const discountPercent = Number.parseFloat(
          String(
            (formDataRegister as Record<string, unknown>)['discount'] ?? '0'
          )
        );
        return [
          {
            name: 'UNICO',
            color: 'UNICO',
            image: filesBase64?.[0] ?? '',
            gradeSizes: [
              {
                size: 'unico',
                stock: String(
                  (formDataRegister as Record<string, unknown>)['stock'] ?? '0'
                ),
                price: {
                  price: isNaN(rawPrice) ? 0 : rawPrice,
                  discount: isNaN(discountPercent) ? 0 : discountPercent,
                },
              },
            ],
          },
        ];
      }

      const map = new Map<string, Grade>();

      for (const v of variants) {
        const keys = Object.keys(v).filter(
          (k) =>
            ![
              'price',
              'promo',
              'discount',
              'stock',
              'media',
              'mediaPreview',
              'image',
              'images',
            ].includes(k)
        );
        const colorKey = resolveColorKey(keys);
        const sizeKey = resolveSizeKey(keys, colorKey);

        const gradeName = String((v as any)[colorKey] ?? 'UNICO');
        const sizeName = String((v as any)[sizeKey] ?? 'unico');

        const rawPrice = Number.parseFloat(String((v as any).price ?? '0'));
        const discountPercent =
          String((v as any).promo).toLowerCase() === 'sim'
            ? Number.parseFloat(String((v as any).discount ?? '0'))
            : 0;

        if (!map.has(gradeName))
          map.set(gradeName, {
            name: gradeName,
            color: gradeName,
            image: '',
            gradeSizes: [],
          });

        const g = map.get(gradeName)!;
        if (!g.image) {
          const vi = pickVariantImage(v);
          if (vi) g.image = vi;
        }

        g.gradeSizes.push({
          size: sizeName,
          stock: String((v as any).stock ?? '0'),
          price: {
            price: isNaN(rawPrice) ? 0 : rawPrice,
            discount: isNaN(discountPercent) ? 0 : discountPercent,
          },
        });
      }

      return Array.from(map.values());
    };

    const buildGallery = (): string[] => {
      const fromVariants: string[] = [];
      if (isGradeFlag) {
        for (const v of variants) {
          const mp = Array.isArray((v as any)?.mediaPreview)
            ? (v as any).mediaPreview
            : [];
          const m = Array.isArray((v as any)?.media) ? (v as any).media : [];
          const direct =
            typeof (v as any)?.image === 'string' ? [(v as any).image] : [];
          for (const src of [...mp, ...m, ...direct]) {
            if (typeof src === 'string' && src) fromVariants.push(src);
            else if (
              src &&
              typeof src === 'object' &&
              'url' in (src as any) &&
              typeof (src as any).url === 'string'
            )
              fromVariants.push((src as any).url);
            else if (
              src &&
              typeof src === 'object' &&
              'src' in (src as any) &&
              typeof (src as any).src === 'string'
            )
              fromVariants.push((src as any).src);
          }
        }
      }
      return unique([...(filesBase64 || []), ...fromVariants]);
    };

    try {
      dispatch(setLoading(true));

      const brand: BrandCreate = {
        name: (formDataRegister as Record<string, string>)[
          FormRegisterProduct.brandName
        ],
      };
      const dimensions: DimensionsCreate = {
        width: (formDataRegister as Record<string, string>)[
          FormRegisterProduct.dimensionsWidth
        ],
        height: (formDataRegister as Record<string, string>)[
          FormRegisterProduct.dimensionsHeight
        ],
        depth: (formDataRegister as Record<string, string>)[
          FormRegisterProduct.dimensionsDepth
        ],
        weight: (formDataRegister as Record<string, string>)[
          FormRegisterProduct.dimensionsWeight
        ],
      };
      const warranty: WarrantyCreate = {
        warranty: (formDataRegister as Record<string, string>)[
          FormRegisterProduct.warranty
        ],
      };

      const gradeDefine: Grade[] = buildGrades();

      if (isGradeFlag && gradeDefine.length <= 0) {
        toast.error('Adicione ao menos uma variação.');
        setErrorRegister({ hiddenField: ['Adicione ao menos uma variação.'] });
        return;
      }

      const summedStock = isGradeFlag
        ? gradeDefine.reduce(
            (s, g) =>
              s + g.gradeSizes.reduce((x, gs) => x + Number(gs.stock || 0), 0),
            0
          )
        : Number(
            (formDataRegister as Record<string, string>)[
              FormRegisterProduct.stock
            ] || 0
          );

      const imagens = buildGallery();

      const payload: ProductPayload = {
        name: (formDataRegister as Record<string, string>)[
          FormRegisterProduct.name
        ],
        description: (formDataRegister as Record<string, string>)[
          FormRegisterProduct.description
        ],
        stock: String(summedStock),
        sku: (formDataRegister as Record<string, string>)[
          FormRegisterProduct.sku
        ],
        promotion:
          ((formDataRegister as Record<string, string>)[
            FormRegisterProduct.promotion
          ] ?? '') !== '0' &&
          String(
            (formDataRegister as Record<string, string>)[
              FormRegisterProduct.promotion
            ] ?? ''
          ).trim() !== '',
        status: handleStatusType(
          (formDataRegister as Record<string, string>)[
            FormRegisterProduct.status
          ]
        ),
        category: selectedCategories,
        brand,
        dimensions,
        imagens,
        tags: inputTags,
        warranty,
        grade: gradeDefine,
        freeShipping:
          String(
            (formDataRegister as Record<string, unknown>)[
              FormRegisterProduct.freeShipping
            ]
          ).toLowerCase() === 'true',
        saleType: (formDataRegister as Record<string, string>)[
          FormRegisterProduct.saleType
        ],
      };

      console.log(payload);
      const response = await api.post('/product', payload);

      if (response.status === 201) {
        onChangeFormInputRegister(FormRegisterProduct.name)('');
        onChangeFormInputRegister(FormRegisterProduct.description)('');
        onChangeFormInputRegister(FormRegisterProduct.sku)('');
        onChangeFormInputRegister(FormRegisterProduct.status)('');
        onChangeFormInputRegister(FormRegisterProduct.promotion)('');
        onChangeFormInputRegister(FormRegisterProduct.brandName)('');
        onChangeFormInputRegister(FormRegisterProduct.dimensionsWidth)('');
        onChangeFormInputRegister(FormRegisterProduct.dimensionsHeight)('');
        onChangeFormInputRegister(FormRegisterProduct.dimensionsDepth)('');
        onChangeFormInputRegister(FormRegisterProduct.dimensionsWeight)('');
        onChangeFormInputRegister(FormRegisterProduct.warranty)('');
        onChangeFormInputRegister(FormRegisterProduct.freeShipping)('');
        onChangeFormInputRegister(FormRegisterProduct.isGrade)('');
        onChangeFormInputRegister(FormInputGradeSize.price)('');
        onChangeFormInputRegister(FormInputGradeSize.discount)('');
        onChangeFormInputRegister(FormInputGradeSize.stock)('');
        onChangeFormInputRegister(FormInputGrade.color)('');
        onChangeFormInputRegister(FormInputGrade.nameGrade)('');
        setSelectedCategories([]);
        setGrade([]);
        setGradeSize([]);
        setInputTags([]);
        setPreviewFiles([]);
        setPreviewFilesGrade('');
        setPreviewGradesSizes([]);
        setFiles([]);
        setFilesGrade([]);
        setFilesBase64([]);
        setFilesBase64Grade('');
        setFormNameFiles({});
        setShippingCost(null);
        setIsGrade(false);
        setAdvancedGrades([]);
        toast.success('Produto cadastrado com sucesso!');
      } else {
        toast.error('Falha ao cadastrar o produto');
      }
    } catch (error: unknown) {
      dispatch(setLoading(false));
      let errorMessage =
        'Falha ao realizar o cadastro, tente novamente mais tarde';
      if (error instanceof Error && (error as AxiosError).response) {
        const responseData = (error as AxiosError).response?.data;
        errorMessage =
          typeof responseData === 'string'
            ? responseData
            : JSON.stringify(responseData);
      } else if (error instanceof Error) {
        errorMessage = error.message;
      }
      toast.error(errorMessage);
      setErrorRegister({ document: [errorMessage] });
    } finally {
      dispatch(setLoading(false));
    }
  };

  const handleAddTag = (tag: string) => {
    if (tag && !inputTags.includes(tag)) {
      setInputTags([...inputTags, tag]);
    } else {
      toast.error('Tag já adicionada ou inválida.');
    }
  };

  const handleRemoveTag = (tagToRemove: string) => {
    setInputTags(inputTags.filter((tag) => tag !== tagToRemove));
  };

  const handleCategorySelect = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const selectedId = e.target.value;
    if (selectedId) {
      const selectedCategory = category.find((cat) => cat.id === selectedId);
      if (
        selectedCategory &&
        !selectedCategories.some((cat) => cat.id === selectedId)
      ) {
        setSelectedCategories([...selectedCategories, selectedCategory]);
      }
    }
  };

  const handleRemoveCategory = (id: string) => {
    setSelectedCategories(selectedCategories.filter((cat) => cat.id !== id));
  };

  const handleAddQuantiValue = (e: FormEvent) => {
    e.preventDefault();
    const newGrade: Grade = {
      color: formDataRegister[FormInputGrade.color] || '',
      name: formDataRegister[FormInputGrade.nameGrade] || '',
      image: filesBase64Grade || '',
      gradeSizes: gradeSize?.length ? gradeSize : [],
    };
    const isDuplicate = grade.some((item) => {
      const isSameColor =
        item.color?.toLowerCase() === newGrade.color?.toLowerCase();
      const isSameName =
        item.name && item.name.toLowerCase() === newGrade.name?.toLowerCase();
      setFilesBase64Grade('');
      return isSameColor || isSameName;
    });

    if (isDuplicate) {
      toast.error('Já existe uma variação com esta cor ou nome.');
      return;
    }

    if (!newGrade) {
      toast.error('Por favor, preencha a grade.');
      return;
    }

    if (newGrade.color === '') {
      if (newGrade.name === '') {
        toast.error('Por favor, preencha a grade com nome');
        return;
      }
    }

    if (newGrade && gradeSize.length > 0) {
      setGrade([...grade, newGrade]);
      onChangeFormInputRegister(FormInputGrade.color)('');
      onChangeFormInputRegister(FormInputGrade.imageBase64Grade)('');
      setGradeSize([]);
      setPreviewGradesSizes([]);
      setPreviewFilesGrade('');
    }
  };

  const handleAddGradeSize = (e: FormEvent) => {
    e.preventDefault();
    const newGradeSize: GradeSize = {
      size: formDataRegister[FormInputGradeSize.size],
      stock: formDataRegister[FormInputGradeSize.stock],
      price: {
        price: Number.parseFloat(formDataRegister[FormInputGradeSize.price]),
        discount: Number.parseInt(
          formDataRegister[FormInputGradeSize.discount]
        ),
      },
    };
    if (newGradeSize && !inputTags.includes(newGradeSize.size.toString())) {
      setGradeSize([...gradeSize, newGradeSize]);
      onChangeFormInputRegister(FormInputGradeSize.size)('');
      onChangeFormInputRegister(FormInputGradeSize.stock)('');
      onChangeFormInputRegister(FormInputGradeSize.price)('');
      onChangeFormInputRegister(FormInputGradeSize.discount)('');
    }

    setPreviewGradesSizes([...previewGradesSizes, newGradeSize]);
  };

  const handleShippingCalc = async (
    altura: string,
    largura: string,
    profundidade: string,
    peso: string
  ) => {
    try {
      const payload = { altura, largura, profundidade, peso };

      const response = await api.post('/product/calc-frete', payload);

      if (response.data.frete && response.data.frete !== 0) {
        setShippingCost(toCurrency(response.data.frete));
      } else if (
        altura === '' ||
        largura === '' ||
        profundidade === '' ||
        peso === ''
      ) {
        setShippingCost('Preencha todos os campos.');
      } else {
        setShippingCost('Valor de frete indisponível ou inválido.');
      }
    } catch (error) {
      console.error('Erro ao calcular frete:', error);
      setShippingCost('Erro ao calcular o frete.');
    }
  };

  const handleRemoveGrade = (index: number): void => {
    setGrade(grade.filter((_, i) => i !== index));
  };

  const handleIsGrade = (inputName: string) => (value: string) => {
    setIsGrade(value === 'true');
  };

  const handleAdvancedGradeChange = (grades: any[]) => {
    setAdvancedGrades(grades);
    onChangeFormInputRegister('advancedGrades')(JSON.stringify(grades));
  };

  useEffect(() => {
    getCategory();
    // eslint-disable-next-line
  }, [])

  return (
    <CreateProductUI
      state={loading}
      formData={formDataRegister}
      formErrors={formErrorRegister}
      formNameFiles={formNameFiles}
      onChangeFormInput={onChangeFormInputRegister}
      onSubmitRegister={handleOnSubmitRegister}
      onChangeFormFileInput={handleOnChangeFileInput}
      filesImage={files}
      categoryOptions={category}
      onDeleteFile={handleDeleteFile}
      previewFiles={previewFiles}
      previewFilesGrade={previewFilesGrade}
      previewGradesSizes={previewGradesSizes}
      selectedCategories={selectedCategories}
      FileUpload={handleFileUpload}
      inputTags={inputTags}
      onAddTag={handleAddTag}
      onRemoveTag={handleRemoveTag}
      onSelectCategory={handleCategorySelect}
      onRemoveCategory={handleRemoveCategory}
      addQuantiValue={handleAddQuantiValue}
      removeGrade={handleRemoveGrade}
      isGrade={isGrade}
      grade={grade}
      shippingCost={shippingCost}
      onShippingCalc={handleShippingCalc}
      addSize={handleAddGradeSize}
      onChangeFormFileInputGradeSize={handleOnChangeFileInputGradeSize}
      filesImageGrade={filesGrade}
      onGrade={handleIsGrade}
      onAdvancedGradeChange={handleAdvancedGradeChange}
    />
  );
};
